/**
 * Loads an SVG icon file's markup and inlines it into an element, instead
 * of a plain Unicode glyph — used where a glyph alone didn't render
 * consistently enough (see eye.svg, drawn by hand in Inkscape). Inlining
 * (rather than an <img src>) keeps the icon's `fill="currentColor"` paths
 * following the containing button's own `color`, so it still recolors with
 * the app's filled/outlined toggle-button convention for free.
 *
 * Cached per URL — every button sharing an icon triggers one fetch.
 */
const iconCache = new Map<string, Promise<string>>();

function loadIconSvg(url: string): Promise<string> {
  let pending = iconCache.get(url);
  if (!pending) {
    pending = fetch(url).then(r => r.text());
    iconCache.set(url, pending);
  }
  return pending;
}

/**
 * Fetch (or reuse the cached fetch of) an icon and set it as `el`'s content.
 * The inlined `<svg>`'s own width/height attributes are overridden to
 * `size` so every button gets a consistent icon size regardless of the
 * source file's intrinsic dimensions.
 */
export function setIcon(el: HTMLElement, url: string, size = '15px'): void {
  loadIconSvg(url).then(svg => {
    el.innerHTML = svg;
    const svgEl = el.querySelector('svg');
    if (svgEl) {
      svgEl.style.width = size;
      svgEl.style.height = size;
      // Nudged 1px up/left — centered-by-eye correction for this glyph's
      // visual weight within the round buttons, same kind of baseline
      // tweak the × glyph elsewhere gets.
      svgEl.style.transform = 'translate(-1px, -1px)';
    }
  }).catch(() => {
    // Leave whatever placeholder content el already had (if any).
  });
}
